/**
 * Sentry initialization for the browser bundle.
 *
 * No-op if VITE_SENTRY_DSN is unset, which is the desired behavior for
 * local dev — you can opt-in by putting a DSN in `.env.local` if you
 * want to test the integration without polluting production.
 *
 * The release tag pulls from VITE_APP_VERSION (set in CI from the git
 * SHA) so Sentry's "issues introduced in release X" view is accurate.
 * Falls back to "dev" locally so dev errors don't pollute the prod
 * release stream.
 */

import * as Sentry from '@sentry/react';

let initialized = false;

export const initSentry = (): void => {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return; // No DSN → silently no-op. Logger fallbacks still work.
  if (initialized) return;

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || 'dev',

    // Performance: full sampling. A 5-user internal app produces trivial
    // trace volume, so 100% is quota-safe and makes the performance p75s
    // + action spans trustworthy (no sampling gaps to reason around).
    tracesSampleRate: 1.0,

    // Session replay: only record when something breaks. 0 sessions
    // unless an error fires, then capture the surrounding window.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Only browserTracing + error capture are eager here. Session Replay
    // and User Feedback are the two heaviest init-time integrations
    // (replay wires DOM mutation/perf observers; feedback injects the
    // floating button into the DOM), and neither is needed for first
    // paint — they're attached post-paint by
    // installDeferredSentryIntegrations() (called from main.tsx after
    // render). Tradeoff, stated honestly: an error thrown in the first
    // ~1-2s before the browser goes idle has no replay attached, and the
    // "Report" button only appears once that deferred install runs.
    // replaysSessionSampleRate / replaysOnErrorSampleRate stay in the
    // options above — they're client-level options, read correctly even
    // when the replay integration is added late.
    integrations: [
      Sentry.browserTracingIntegration(),
    ],

    // Suppress noise that isn't ours to fix.
    ignoreErrors: [
      // Browser-extension noise.
      /Non-Error promise rejection captured/,
      /ResizeObserver loop limit exceeded/,
      /ResizeObserver loop completed with undelivered notifications/,
      // User aborted in-flight work (intentional, not a bug).
      'AbortError',
      // Firebase Auth: expected paths during password reset / failed login.
      'auth/user-not-found',
      'auth/wrong-password',
      'auth/invalid-credential',
      // Stale-tab-after-deploy: user's tab references a chunk file from a
      // previous build that's been overwritten on the CDN. We handle this
      // class with a reload prompt (see src/utils/staleTabReload.ts) so
      // there's no point reporting it. Keep this list in sync with
      // STALE_TAB_PATTERNS in staleTabReload.ts.
      /Failed to fetch dynamically imported module/,
      /Loading chunk \d+ failed/,
      /Importing a module script failed/,
      /error loading dynamically imported module/i,
      /Expected a JavaScript-or-Wasm module script/i,
      // Firefox phrasing of the MIME-mismatch case (when the SPA's
      // index.html comes back where a JS chunk was expected):
      //   "'text/html' is not a valid JavaScript MIME type."
      /is not a valid JavaScript MIME type/i,
      // Generic transient network blip — user briefly lost connectivity,
      // app retries usually succeed. Not actionable.
      /NetworkError when attempting to fetch resource/,
      /Load failed/, // Safari's variant of the above
      // Firebase Firestore on iOS Safari — the SDK's IndexedDB
      // connection gets terminated by the OS when a tab is
      // backgrounded or sits idle. Baseline mobile Safari quirk, not
      // actionable on our side; the error message itself ("Refresh
      // the page to try again") tells the user what to do. Filtering
      // here keeps the Sentry tracker focused on real bugs.
      /Connection to Indexed Database server lost/,
      // QR scanner — user is on a device without a camera (laptop with
      // no webcam, or browser blocked camera access). The throw is
      // correct app behavior; reporting it is just noise. The scanner
      // page surfaces a friendly error message either way.
      'No camera devices found',
      // QR scanner — user denied / hasn't yet granted camera permission.
      // Same logic: handled in-UI, not actionable in Sentry.
      'NotAllowedError',
      'Permission denied',
    ],

    // Drop events from third-party origins (browser extensions inject
    // scripts that can crash and look like our errors).
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
  });

  initialized = true;
};

/**
 * Attach Session Replay + User Feedback after first paint.
 *
 * No-op if initSentry() didn't run (no DSN → nothing to add). Called
 * from main.tsx AFTER createRoot().render() so the heavy setup work
 * (replay observers, feedback DOM injection) lands on idle instead of
 * competing with boot. Adding these late is Sentry's documented
 * lazy-load path: addIntegration() runs the integration's setup, and
 * the replay sampling rates already live in the init options above.
 * See the tradeoff note on the init `integrations` array.
 */
export const installDeferredSentryIntegrations = (): void => {
  if (!initialized) return;

  const run = () => {
    Sentry.addIntegration(
      Sentry.replayIntegration({
        // Mask user-entered text (tasting notes, prices) and media so
        // replays don't capture sensitive operational data.
        maskAllText: true,
        blockAllMedia: true,
      }),
    );
    // User Feedback: always-visible floating "Report a problem"
    // button + the same form auto-popped on render-time crashes via
    // ErrorBoundary. For a 5-user internal team the floating button
    // is genuine signal, not clutter — they can flag UX paper-cuts
    // and bugs without leaving the app.
    Sentry.addIntegration(
      Sentry.feedbackIntegration({
        autoInject: true,
        colorScheme: 'system',
        showBranding: false,
        // Floating button copy + position. Short label so the floating
        // button doesn't clip at narrower (tablet portrait) widths;
        // the form itself still uses the longer "Report a problem"
        // headline (formTitle below).
        triggerLabel: 'Report',
        triggerAriaLabel: 'Open the feedback form to report a bug or send a note',
        buttonLabel: 'Report',
        // Form copy
        formTitle: 'Report a problem',
        submitButtonLabel: 'Send to dev',
        cancelButtonLabel: 'Cancel',
        messageLabel: 'What happened?',
        messagePlaceholder:
          "e.g. 'I clicked Reuse on barrel WB-014 and the page went blank' — include the barrel ID if relevant.",
        // Pre-fill name + email when the user is logged in (Sentry.setUser
        // is called from AuthContext after auth resolves).
        useSentryUser: { email: 'email', name: 'username' },
        showName: true,
        showEmail: true,
        isEmailRequired: false,
        isNameRequired: false,
        successMessageText: "Thanks — your note was sent. We'll take a look.",
        // Optional: let the user attach a screenshot of what they're
        // looking at right now. Hugely improves bug reports.
        enableScreenshot: true,
      }),
    );
  };

  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(run, { timeout: 2500 });
  } else {
    // Safari has no requestIdleCallback — a short delay clears first paint.
    setTimeout(run, 2500);
  }
};

/**
 * Set the current user on Sentry's scope. Call from AuthContext after
 * auth resolves so every error report includes who hit it.
 */
export const setSentryUser = (
  user: { uid: string; email?: string | null; role?: string } | null,
): void => {
  if (!initialized) return;
  if (!user) {
    Sentry.setUser(null);
    return;
  }
  Sentry.setUser({
    id: user.uid,
    email: user.email ?? undefined,
  });
  if (user.role) Sentry.setTag('user.role', user.role);
};

/** Re-export so call sites import from one path. */
export { Sentry };
